import React, { useEffect } from 'react';
import {useAppDispatch, useAppSelector} from '../../../../../../hooks/hooks';
import {updateAbsenceType} from '../../../../../../features/types';
import {AbsenceBehavior, TiminatorAbsenceType} from '../../../../../../features/types/types';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {FormInput} from '../../../../../../components/shared/form-controls/form-input';
import {FormCheckbox} from '../../../../../../components/shared/form-controls/form-checkbox';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseForm from '../../../../../../components/shared/form-controls/base-form';
import { NAVIGATION_ROUTES } from '../../../../../../routes/NavigationRoute';
import { canManageAbsenceTypes } from '../../../../../../features/profile/entityPermissions';
import { DropdownField } from '../../../../../../components/shared/nextgen/dropdown-field';

interface EditForm {
    name: string;
    isDefault: boolean;
    isActive: boolean;
    behavior: { id: AbsenceBehavior, name: string };
}

export default function AbsenceTypeEditForm() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as {absenceType: TiminatorAbsenceType} | undefined;
    const profile = useAppSelector(store => store.profile);
    const canEditAbsenceTypes = canManageAbsenceTypes(profile);
    const behaviorOptions = [
        { id: AbsenceBehavior.None, name: 'None' },
        { id: AbsenceBehavior.Vacation, name: 'Use for vacation' },
        { id: AbsenceBehavior.LimitToPlannedHours, name: 'Use planned hours only' },
    ];

    useEffect(() => {
        if (!state?.absenceType || !canEditAbsenceTypes) {
            navigate(NAVIGATION_ROUTES.ABSENCE_TYPES);
        }
    }, [canEditAbsenceTypes, navigate, state]);

    const initialValues: EditForm = {
        name: state?.absenceType?.name ?? '',
        isDefault: state?.absenceType?.default ?? false,
        isActive: state?.absenceType?.active ?? false,
        behavior: behaviorOptions.find((item) => item.id === state?.absenceType?.behavior) ?? behaviorOptions[0],
    };

    const initialHeader = state?.absenceType?.name ?? '';

    if (!state?.absenceType || !canEditAbsenceTypes) {
        return null;
    }

    const save = async (values: EditForm) => {
        const type: TiminatorAbsenceType = {
            id: state?.absenceType?.id ?? 0,
            name: values.name,
            active: values.isActive,
            default: values.isDefault,
            behavior: values.behavior.id,
        }

        return dispatch(updateAbsenceType(type))
    }

    const validationScheme: Yup.Schema<EditForm> = Yup.object({
        name: Yup.string()
            .max(45)
            .required('Required'),
        isDefault: Yup.boolean().required(),
        isActive: Yup.boolean().required(),
        behavior: Yup.mixed<EditForm['behavior']>().required(),
    })

    return <Formik
        initialValues={initialValues}
        validationSchema={validationScheme}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={save}>
        <Form>
            <BaseForm contentGap={24}
                formType='edit'
                formHeader='edit_absence_type'
                elementName={initialHeader} 
                savingTitle='Absence type' 
                onClose={() => navigate(NAVIGATION_ROUTES.ABSENCE_TYPES)} 
                onAfterSave={() => navigate(NAVIGATION_ROUTES.ABSENCE_TYPES)}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px' }}>
                    <FormInput name='name' label='Name' placeholder='Absence type' />
                    <DropdownField name='behavior' label='Behavior' items={behaviorOptions} required placeholder='' />
                    <fieldset>
                        <FormCheckbox name='isDefault' label='Default' />
                        <FormCheckbox name='isActive' label='Active'/>
                    </fieldset>
                </div>
            </BaseForm>
        </Form>
    </Formik>
}
